/**
 * i18n initialization — English/Spanish with browser language detection.
 *
 * - Persisted language preference in localStorage ('app-language')
 * - Falls back to browser language, then English
 * - Flat namespace via nested key access: t('dashboard.myTasks')
 *
 * Import this module once at app startup (main.tsx) to ensure i18next is
 * initialized before any component renders.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './locales/en'
import es from './locales/es'

const STORAGE_KEY = 'app-language'
const SUPPORTED_LANGUAGES = ['en', 'es'] as const

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

/**
 * Resolve initial language from localStorage -> browser -> 'en'.
 */
function detectLanguage(): SupportedLanguage {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && isSupportedLanguage(stored)) return stored

  const browserLangs = navigator.languages ?? [navigator.language]
  for (const lang of browserLangs) {
    const prefix = lang.slice(0, 2).toLowerCase()
    if (isSupportedLanguage(prefix)) return prefix
  }

  return 'en'
}

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

/**
 * Change language and persist to localStorage.
 */
export function setLanguage(lang: SupportedLanguage): void {
  localStorage.setItem(STORAGE_KEY, lang)
  void i18n.changeLanguage(lang)
}

/**
 * Read the currently active language.
 */
export function getLanguage(): SupportedLanguage {
  const current = i18n.language
  return isSupportedLanguage(current) ? current : 'en'
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
  },
  lng: detectLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes
  },
})

export { SUPPORTED_LANGUAGES, STORAGE_KEY }
export default i18n
